import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, Pause, Square, RotateCcw, Wrench, Car, User, ArrowLeft, Clock,
  Search, ArrowRightLeft, AlertTriangle, History, ListChecks, ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { ColorChip } from "@/components/ColorChip";
import { useWorkshopPauseReminder } from "@/hooks/useWorkshopPauseReminder";

// Formats seconds as HH:MM:SS. Zero-padded, monospaced when rendered.
function fmtHMS(totalSec) {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// Formats seconds as "Xh Ym" (compact). Used for pause reminders and session totals.
function fmtHumanDuration(totalSec) {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Formats minutes as "HH:MM h" for the estimate label
function fmtEstimatedHM(totalMin) {
  const m = Math.max(0, Math.round(totalMin));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")} h`;
}

const fmtTimeHM = (iso) => iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

// Colour palette per state (spec: green=running, orange=paused, gray=stopped)
const THEMES = {
  running: {
    bg: "bg-emerald-950",
    accent: "text-emerald-300",
    card: "bg-emerald-900/60 border-emerald-700",
    ring: "ring-emerald-500/40",
    label: "Läuft",
    pill: "bg-emerald-500 text-emerald-950",
  },
  paused: {
    bg: "bg-amber-950",
    accent: "text-amber-300",
    card: "bg-amber-900/60 border-amber-700",
    ring: "ring-amber-500/40",
    label: "Pausiert",
    pill: "bg-amber-500 text-amber-950",
  },
  idle: {
    bg: "bg-slate-950",
    accent: "text-slate-300",
    card: "bg-slate-900/60 border-slate-700",
    ring: "ring-slate-500/40",
    label: "Bereit",
    pill: "bg-slate-500 text-slate-50",
  },
  finished: {
    bg: "bg-slate-950",
    accent: "text-slate-300",
    card: "bg-slate-900/60 border-slate-700",
    ring: "ring-slate-500/40",
    label: "Abgeschlossen",
    pill: "bg-slate-600 text-slate-50",
  },
};

export default function WorkshopMode() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jidParam = searchParams.get("job");
  const requestedReturn = searchParams.get("returnTo");
  const exitTarget = requestedReturn?.startsWith("/") && !requestedReturn.startsWith("//")
    ? requestedReturn
    : "/auftraege";
  const [selectedJobId, setSelectedJobId] = useState(jidParam || null);
  const [tick, setTick] = useState(0);

  // Try to fetch the currently active job (server-side); this seeds the view.
  const { data: activeJob } = useQuery({
    queryKey: ["active-timer-job"],
    queryFn: async () => (await api.get("/jobs/active-timer")).data,
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (!selectedJobId && activeJob?.id) setSelectedJobId(activeJob.id);
  }, [activeJob, selectedJobId]);

  const { data: job, isLoading: jobLoading, refetch: refetchJob } = useQuery({
    queryKey: ["job", selectedJobId],
    queryFn: async () => (await api.get(`/jobs/${selectedJobId}`)).data,
    enabled: !!selectedJobId,
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });

  // Installed PWAs do not always emit a normal window-focus event. Refresh the
  // selected job explicitly whenever the app becomes visible again.
  useEffect(() => {
    const refreshVisibleJob = () => {
      if (document.visibilityState === "visible" && selectedJobId) refetchJob();
    };
    document.addEventListener("visibilitychange", refreshVisibleJob);
    window.addEventListener("pageshow", refreshVisibleJob);
    return () => {
      document.removeEventListener("visibilitychange", refreshVisibleJob);
      window.removeEventListener("pageshow", refreshVisibleJob);
    };
  }, [refetchJob, selectedJobId]);

  const state = job?.timer_state || "idle";
  const theme = THEMES[state] || THEMES.idle;

  // Live tick when running or paused (paused-since reminder must update)
  useEffect(() => {
    if (state !== "running" && state !== "paused") return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  const liveExtra = state === "running" && job?.timer_last_resumed_at
    ? Math.max(0, (Date.now() - new Date(job.timer_last_resumed_at).getTime()) / 1000)
    : 0;
  const totalSec = (job?.total_work_seconds || 0) + liveExtra;
  const pausedSec = state === "paused" && job?.paused_since
    ? Math.max(0, (Date.now() - new Date(job.paused_since).getTime()) / 1000)
    : 0;
  // eslint-disable-next-line no-unused-vars
  const _keepTicking = tick;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["job", selectedJobId] });
    qc.invalidateQueries({ queryKey: ["active-timer-job"] });
    qc.invalidateQueries({ queryKey: ["timer-candidates"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const call = async (endpoint, jid = selectedJobId) => (await api.post(`/jobs/${jid}/timer/${endpoint}`)).data;

  // Overview poll — all active/paused/waiting candidates
  const { data: candidates = [] } = useQuery({
    queryKey: ["timer-candidates"],
    queryFn: async () => (await api.get("/jobs/timer-candidates")).data,
    refetchInterval: 5000,
  });

  useWorkshopPauseReminder(candidates);

  const runningJobs = candidates.filter((j) => j.timer_state === "running");
  const runningOtherThan = (jid) => runningJobs.filter((j) => j.id !== jid);

  const startMut  = useMutation({ mutationFn: () => call("start"),  onSuccess: () => { toast.success("Timer gestartet");    invalidate(); }, onError: (e) => toast.error(apiError(e)) });
  const pauseMut  = useMutation({ mutationFn: () => call("pause"),  onSuccess: () => { toast.success("Timer pausiert");     invalidate(); }, onError: (e) => toast.error(apiError(e)) });
  const resumeMut = useMutation({ mutationFn: () => call("resume"), onSuccess: () => { toast.success("Timer fortgesetzt");  invalidate(); }, onError: (e) => toast.error(apiError(e)) });
  const finishMut = useMutation({ mutationFn: () => call("finish"), onSuccess: () => { toast.success("Timer abgeschlossen"); invalidate(); }, onError: (e) => toast.error(apiError(e)) });
  const resetMut  = useMutation({ mutationFn: () => call("reset"),  onSuccess: () => { toast.success("Timer zurückgesetzt"); invalidate(); }, onError: (e) => toast.error(apiError(e)) });
  // Sprint 24 — complete/reopen bundle timer + status changes into one call.
  const completeMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${selectedJobId}/complete`)).data,
    onSuccess: () => { toast.success("Auftrag abgeschlossen"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const reopenMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${selectedJobId}/reopen`)).data,
    onSuccess: () => { toast.success("Auftrag wieder geöffnet"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const switchMut = useMutation({
    mutationFn: (jid) => call("switch", jid),
    onSuccess: (j) => { toast.success(`Gewechselt zu ${j.title || j.customer_name}`); setSelectedJobId(j.id); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const confirmParallelTimer = (jid) => {
    const others = runningOtherThan(jid);

    if (others.length >= 2) {
      toast.error("Es laufen bereits zwei Timer gleichzeitig. Bitte zuerst einen Timer pausieren.");
      return false;
    }

    if (others.length === 1) {
      return window.confirm(
        `"${others[0].title || others[0].customer_name}" läuft bereits. Beide Timer gleichzeitig laufen lassen?`
      );
    }

    return true;
  };

  const guardedStart = (mutate) => {
    if (!confirmParallelTimer(selectedJobId)) return;
    mutate();
  };

  const quickSwitch = (jid, label) => {
    if (!confirmParallelTimer(jid)) return;
    switchMut.mutate(jid);
  };

  // No job selected → show picker
  if (!selectedJobId) return <WorkshopPicker onPick={setSelectedJobId} onExit={() => navigate(exitTarget)} />;
  if (jobLoading || !job) {
    return (
      <div className="fixed inset-0 bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-slate-400">Lädt Auftrag…</div>
      </div>
    );
  }

  const estimatedMin = job.estimated_duration_min || 0;
  const jobServices = (job.services || []).filter((service) => service?.service_name);
  const stateInstruction = {
    idle: "Bereit zum Starten. Der Timer läuft erst nach Ihrer Bestätigung.",
    running: "Die Arbeitszeit wird gerade erfasst.",
    paused: "Die Zeiterfassung ist pausiert und kann jederzeit fortgesetzt werden.",
    finished: "Die Zeiterfassung ist beendet. Schließen Sie nun den Auftrag ab.",
  }[state] || "";
  const workflowStep = job.status === "fertig" ? 3 : (state === "idle" ? 1 : 2);

  return (
    <div className={`stellar-workshop fixed inset-0 ${theme.bg} text-white flex flex-col overflow-hidden transition-colors duration-500`} data-testid="workshop-mode">
      {/* Top bar */}
      <div className="stellar-workshop-topbar flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3 sm:py-4 border-b border-white/10">
        <button
          onClick={() => navigate(exitTarget)}
          data-testid="workshop-exit"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Verlassen</span>
        </button>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${theme.pill}`} data-testid="workshop-state-pill">
            <span className={`h-2 w-2 rounded-full ${state === "running" ? "animate-pulse bg-current" : "bg-current opacity-70"}`} />
            {theme.label}
          </span>
          <button
            onClick={() => setSelectedJobId(null)}
            data-testid="workshop-change-job"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white/70 hover:bg-white/10 hover:text-white"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" /> Auftrag wechseln
          </button>
        </div>
      </div>

      {/* Main grid: timer/controls (left) + overview + sessions (right) */}
      <div className="flex-1 overflow-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-0">
        {/* Left column — Timer + controls */}
        <div className="flex flex-col items-center justify-center px-4 md:px-6 py-5 md:py-8 gap-5 md:gap-6 text-center">
          <div className="stellar-workshop-progress w-full max-w-3xl" aria-label="Arbeitsfortschritt">
            {[
              { number: 1, label: "Auftrag bereit" },
              { number: 2, label: "In Arbeit" },
              { number: 3, label: "Abgeschlossen" },
            ].map((step) => (
              <div
                key={step.number}
                className={`stellar-workshop-progress-step ${workflowStep >= step.number ? "is-active" : ""} ${workflowStep > step.number ? "is-done" : ""}`}
              >
                <span>{workflowStep > step.number ? <CheckCircle2 className="h-4 w-4" /> : step.number}</span>
                <small>{step.label}</small>
              </div>
            ))}
          </div>

          {/* Job info card */}
          <div className={`stellar-workshop-job-card w-full max-w-3xl rounded-2xl ${theme.card} border p-5 md:p-6`} data-testid="workshop-job-card">
            <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center justify-center gap-2">
              <Wrench className="h-3 w-3" /> Aktiver Auftrag
            </div>
            <div className="text-2xl md:text-4xl font-display font-bold" data-testid="workshop-job-title">
              {job.title || job.customer_name || "Auftrag"}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 md:gap-8 text-left max-w-2xl mx-auto">
              <div className="flex items-start gap-2 min-w-0">
                <User className="h-5 w-5 text-white/50 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Kunde</div>
                  <div className="text-lg md:text-xl font-semibold truncate" data-testid="workshop-customer">{job.customer_name || "—"}</div>
                </div>
              </div>
              <div className="flex items-start gap-2 min-w-0">
                <Car className="h-5 w-5 text-white/50 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Fahrzeug</div>
                  <div className="text-lg md:text-xl font-semibold truncate" data-testid="workshop-vehicle">{job.vehicle_display || "—"}</div>
                  {job.vehicle_color && <ColorChip value={job.vehicle_color} className="mt-1 text-white/70" testId="workshop-vehicle-color" />}
                </div>
              </div>
            </div>
            {jobServices.length > 0 && (
              <div className="mt-5 border-t border-white/10 pt-4 text-left max-w-2xl mx-auto">
                <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45">
                  <ListChecks className="h-4 w-4" /> Leistungen ({jobServices.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {jobServices.map((service, index) => (
                    <span
                      key={`${service.service_id || service.service_name}-${index}`}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black/15 px-3 py-2 text-sm font-semibold text-white/90"
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: service.color || "#10B981" }}
                      />
                      {service.service_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Estimated (optional) + Running timer stacked labels */}
          <div className="w-full max-w-3xl flex flex-col items-center gap-3">
            {estimatedMin > 0 && (
              <div className="text-center" data-testid="workshop-estimated">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Geschätzt</div>
                <div className="font-mono font-semibold text-white/70 text-2xl md:text-3xl mt-1">{fmtEstimatedHM(estimatedMin)}</div>
              </div>
            )}
            {/* Sprint 24b — show "Arbeitszeit gesamt" summary when the job is completed,
                and keep the giant running timer only for active states. */}
            {job.status === "fertig" ? (
              <div className="text-center" data-testid="workshop-total-worktime">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Arbeitszeit gesamt</div>
                <div className={`font-mono font-bold tabular-nums tracking-tight leading-none mt-1 ${theme.accent}`}
                     style={{ fontSize: "clamp(3rem, 12vw, 10rem)" }}>
                  {fmtHumanDuration(job.total_work_seconds || 0)}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                  {state === "running" ? "Arbeitszeit · läuft" : state === "paused" ? "Arbeitszeit · pausiert" : "Arbeitszeit"}
                </div>
                <div className={`font-mono font-bold tabular-nums tracking-tight leading-none mt-1 ${theme.accent}`}
                     style={{ fontSize: "clamp(3.5rem, 15vw, 12rem)" }}
                     data-testid="workshop-timer-display">
                  {fmtHMS(totalSec)}
                </div>
              </div>
            )}
            {state === "paused" && job.paused_since && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-sm"
                data-testid="workshop-paused-since"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Pausiert seit {fmtHumanDuration(pausedSec)}
              </div>
            )}
            {job.status !== "fertig" && (
              <div className="max-w-xl rounded-lg border border-white/10 bg-black/15 px-4 py-2 text-sm text-white/65">
                {stateInstruction}
              </div>
            )}
          </div>

          {/* Big controls */}
          <div className="stellar-workshop-controls flex flex-wrap items-center justify-center gap-4 mt-2">
            {job.status === "fertig" ? (
              <div className="text-white/70 text-sm text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-600/40 border border-slate-400/40 text-slate-100 font-semibold" data-testid="workshop-completed-badge">
                  <Square className="h-4 w-4" /> Abgeschlossen
                </div>
                <div className="mt-3 text-xs text-white/50">
                  Timer gesperrt. Auftrag wurde am {(job.completed_at || job.timer_finished_at) ? new Date(job.completed_at || job.timer_finished_at).toLocaleString("de-DE") : "—"} abgeschlossen.
                </div>
                <div className="mt-4 flex gap-2 justify-center">
                  <button
                    onClick={() => { if (confirm("Auftrag wieder öffnen? Der Timer wird freigeschaltet und der Status auf 'In Bearbeitung' gesetzt.")) reopenMut.mutate(); }}
                    disabled={reopenMut.isPending}
                    data-testid="workshop-reopen"
                    className="text-sm px-4 py-2 rounded border border-white/30 hover:bg-white/10 inline-flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" /> Auftrag wieder öffnen
                  </button>
                  <Link
                    to={`/auftraege/${job.id}`}
                    className="text-sm px-4 py-2 rounded border border-white/20 hover:bg-white/10"
                  >
                    Auftrag öffnen
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {state === "idle" && (
                  <BigButton onClick={() => guardedStart(() => startMut.mutate())} disabled={startMut.isPending} color="green" testid="workshop-start">
                    <Play className="h-8 w-8" /> Starten
                  </BigButton>
                )}
                {state === "running" && (
                  <>
                    <BigButton onClick={() => pauseMut.mutate()} disabled={pauseMut.isPending} color="orange" testid="workshop-pause">
                      <Pause className="h-8 w-8" /> Pause
                    </BigButton>
                    <BigButton
                      onClick={() => { if (confirm("Arbeit abschließen? Timer wird gestoppt und der Auftrag als 'Abgeschlossen' markiert.")) completeMut.mutate(); }}
                      disabled={completeMut.isPending}
                      color="dark"
                      testid="workshop-complete"
                    >
                      <Square className="h-8 w-8" /> Arbeit abschließen
                    </BigButton>
                  </>
                )}
                {state === "paused" && (
                  <>
                    <BigButton onClick={() => guardedStart(() => resumeMut.mutate())} disabled={resumeMut.isPending} color="green" testid="workshop-resume">
                      <Play className="h-8 w-8" /> Fortsetzen
                    </BigButton>
                    <BigButton
                      onClick={() => { if (confirm("Arbeit abschließen? Timer wird gestoppt und der Auftrag als 'Abgeschlossen' markiert.")) completeMut.mutate(); }}
                      disabled={completeMut.isPending}
                      color="dark"
                      testid="workshop-complete"
                    >
                      <Square className="h-8 w-8" /> Arbeit abschließen
                    </BigButton>
                  </>
                )}
                {state === "finished" && (
                  <div className="text-white/70 text-sm text-center">
                    Timer abgeschlossen am {job.timer_finished_at ? new Date(job.timer_finished_at).toLocaleString("de-DE") : "—"}
                    <div className="mt-3 flex gap-2 justify-center">
                      <BigButton
                        onClick={() => { if (confirm("Arbeit abschließen? Der Auftrag wird als 'Abgeschlossen' markiert.")) completeMut.mutate(); }}
                        disabled={completeMut.isPending}
                        color="dark"
                        testid="workshop-complete"
                      >
                        <Square className="h-6 w-6" /> Arbeit abschließen
                      </BigButton>
                      <button
                        onClick={() => { if (confirm("Timer für diesen Auftrag zurücksetzen?")) resetMut.mutate(); }}
                        data-testid="workshop-reset"
                        className="text-xs px-3 py-1.5 rounded border border-white/20 hover:bg-white/10 inline-flex items-center gap-1"
                      >
                        <RotateCcw className="h-3 w-3" /> Zurücksetzen
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Session history */}
          <SessionHistoryPanel job={job} liveExtra={liveExtra} />

          {/* Footer link to job page */}
          {state !== "finished" && (
            <Link
              to={`/auftraege/${job.id}`}
              className="text-xs text-white/40 hover:text-white/70 underline underline-offset-4"
              data-testid="workshop-open-job"
            >
              Auftragsdetails öffnen
            </Link>
          )}
        </div>

        {/* Right column — Workshop overview + quick switch */}
        <WorkshopOverview
          candidates={candidates}
          selectedJobId={selectedJobId}
          onSwitch={quickSwitch}
          onOpen={setSelectedJobId}
        />
      </div>
    </div>
  );
}

// ── Session History panel ────────────────────────────────────────────
function SessionHistoryPanel({ job, liveExtra }) {
  const sessions = job.sessions || [];
  if (sessions.length === 0) return null;
  // Group by local date
  const groups = {};
  for (const s of sessions) {
    const key = fmtDate(s.started_at);
    (groups[key] ||= []).push(s);
  }
  const dates = Object.keys(groups);
  const total = sessions.reduce((a, s) => a + (s.duration_s || 0), 0) + (liveExtra || 0);
  return (
    <div className="w-full max-w-3xl rounded-xl border border-white/10 bg-black/20 p-4 text-left" data-testid="workshop-sessions">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-white/50" />
        <div className="text-xs uppercase tracking-widest text-white/60">Sitzungen</div>
        <div className="ml-auto text-xs text-white/60">
          Gesamt: <span className="font-mono font-semibold text-white/90" data-testid="workshop-sessions-total">{fmtHumanDuration(total)}</span>
        </div>
      </div>
      <div className="space-y-3 max-h-56 overflow-auto pr-1">
        {dates.map((d) => (
          <div key={d}>
            <div className="text-[11px] text-white/40 uppercase tracking-widest mb-1">{d}</div>
            <div className="space-y-1">
              {groups[d].map((s, idx) => {
                const running = !s.ended_at;
                const dur = running ? liveExtra : (s.duration_s || 0);
                return (
                  <div key={idx} className="flex items-center gap-3 text-sm" data-testid={`workshop-session-row-${idx}`}>
                    <span className="font-mono text-white/80 w-28 tabular-nums">
                      {fmtTimeHM(s.started_at)} – {running ? <span className="text-emerald-300">Läuft</span> : fmtTimeHM(s.ended_at)}
                    </span>
                    <span className="text-white/50 font-mono text-xs">{fmtHumanDuration(dur)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Workshop Overview panel ──────────────────────────────────────────
function WorkshopOverview({ candidates, selectedJobId, onSwitch, onOpen }) {
  const running = candidates.filter((j) => j.timer_state === "running");
  const paused = candidates.filter((j) => j.timer_state === "paused");
  const waiting = candidates.filter((j) => j.timer_state === "idle" || !j.timer_state);
  return (
    <div className="border-t lg:border-t-0 lg:border-l border-white/10 bg-black/25 p-4 md:p-5 overflow-auto" data-testid="workshop-overview">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-xs uppercase tracking-[0.3em] text-white/50">Übersicht</div>
      </div>

      <OverviewGroup
        icon={<span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />}
        label={`Läuft (${running.length})`}
        items={running}
        selectedJobId={selectedJobId}
        onSwitch={onSwitch}
        onOpen={onOpen}
        testid="overview-running"
      />
      <OverviewGroup
        icon={<span className="h-2.5 w-2.5 rounded-full bg-amber-400" />}
        label={`Pausiert (${paused.length})`}
        items={paused}
        selectedJobId={selectedJobId}
        onSwitch={onSwitch}
        onOpen={onOpen}
        testid="overview-paused"
        showPausedSince
        showSwitch
      />
      <OverviewGroup
        icon={<span className="h-2.5 w-2.5 rounded-full bg-white/40" />}
        label={`Wartend (${waiting.length})`}
        items={waiting}
        selectedJobId={selectedJobId}
        onSwitch={onSwitch}
        onOpen={onOpen}
        testid="overview-waiting"
        showSwitch
      />
    </div>
  );
}

function OverviewGroup({ icon, label, items, selectedJobId, onSwitch, onOpen, testid, showSwitch, showPausedSince }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-white/50">
        {icon} {label}
      </div>
      <ul className="space-y-1.5">
        {items.map((j) => {
          const isActive = j.id === selectedJobId;
          const pausedFor = j.paused_since
            ? Math.max(0, (Date.now() - new Date(j.paused_since).getTime()) / 1000)
            : 0;
          return (
            <li key={j.id}>
              <div className={`rounded-xl border ${isActive ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/5"} px-3 py-3 flex items-center gap-2`}
                data-testid={`overview-item-${j.id}`}
              >
                <button
                  type="button"
                  onClick={() => onOpen(j.id)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`overview-open-${j.id}`}
                >
                  <div className="text-sm font-medium truncate">{j.title || j.customer_name || "Auftrag"}</div>
                  <div className="text-[11px] text-white/50 truncate">
                    {j.customer_name || "—"} · {j.vehicle_display || "—"}
                    {showPausedSince && pausedFor > 60 && <span className="ml-1 text-amber-300">· Pausiert seit {fmtHumanDuration(pausedFor)}</span>}
                    {!showPausedSince && (j.total_work_seconds > 0) && <span className="ml-1 font-mono">· {fmtHumanDuration(j.total_work_seconds)}</span>}
                  </div>
                </button>
                {!showSwitch && <ChevronRight className="h-4 w-4 shrink-0 text-white/30" />}
                {showSwitch && !isActive && (
                  <button
                    type="button"
                    onClick={() => onSwitch(j.id, j.title || j.customer_name || "Auftrag")}
                    data-testid={`overview-switch-${j.id}`}
                    title="Wechseln zu diesem Auftrag"
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-white/20 hover:bg-white/10"
                  >
                    <ArrowRightLeft className="h-3 w-3" /> Wechseln
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BigButton({ onClick, disabled, color, testid, children }) {
  const palette = {
    green:  "bg-emerald-500 hover:bg-emerald-400 text-emerald-950 shadow-emerald-500/50",
    orange: "bg-amber-500 hover:bg-amber-400 text-amber-950 shadow-amber-500/50",
    dark:   "bg-slate-700 hover:bg-slate-600 text-white shadow-black/40",
  }[color] || "bg-slate-700 hover:bg-slate-600 text-white";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 rounded-2xl font-display font-bold text-xl sm:text-2xl md:text-3xl shadow-lg ${palette} disabled:opacity-50 disabled:cursor-not-allowed transition-transform active:scale-95`}
    >
      {children}
    </button>
  );
}

function WorkshopPicker({ onPick, onExit }) {
  const [q, setQ] = useState("");
  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["timer-candidates"],
    queryFn: async () => (await api.get("/jobs/timer-candidates")).data,
  });
  const filtered = q
    ? candidates.filter((j) => {
        const s = q.toLowerCase();
        return (j.title || "").toLowerCase().includes(s)
          || (j.customer_name || "").toLowerCase().includes(s)
          || (j.vehicle_display || "").toLowerCase().includes(s);
      })
    : candidates;

  return (
    <div className="fixed inset-0 bg-slate-950 text-white flex flex-col" data-testid="workshop-picker">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
        <button onClick={onExit} data-testid="workshop-picker-exit" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Verlassen
        </button>
        <div className="ml-auto text-xs text-white/40">Werkstatt-Modus</div>
      </div>
      <div className="flex-1 overflow-auto px-4 md:px-8 py-6 sm:py-8 max-w-4xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <Clock className="h-6 w-6 text-white/60 shrink-0" />
          <h1 className="font-display text-xl sm:text-3xl font-bold" title="Aktiven Auftrag auswählen und Werkstatt-Timer starten.">Auftrag auswählen</h1>
        </div>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Auftrag / Kunde / Kennzeichen suchen…"
            data-testid="workshop-picker-search"
            className="w-full pl-10 pr-3 py-3 rounded-lg bg-slate-900 border border-slate-800 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>
        {isLoading ? (
          <div className="text-white/40 text-center py-12">Lädt…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <Wrench className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <div className="text-sm">Keine aktiven Aufträge gefunden.</div>
            <Link to="/auftraege" className="mt-4 inline-block text-emerald-400 hover:text-emerald-300 text-sm">Zur Auftragsliste →</Link>
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((j) => {
              const running = j.timer_state === "running";
              const paused = j.timer_state === "paused";
              return (
                <li key={j.id}>
                  <button
                    onClick={() => onPick(j.id)}
                    data-testid={`workshop-pick-${j.id}`}
                    className="w-full text-left rounded-xl border border-slate-800 bg-slate-900/60 hover:border-emerald-500/40 hover:bg-slate-800 p-4 transition-colors flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{j.title || j.customer_name}</div>
                      <div className="text-xs text-white/50 truncate">
                        {j.customer_name} · {j.vehicle_display || "—"}
                      </div>
                    </div>
                    {running && <span className="px-2 py-1 text-[10px] rounded-full bg-emerald-500 text-emerald-950 font-semibold">Läuft</span>}
                    {paused && <span className="px-2 py-1 text-[10px] rounded-full bg-amber-500 text-amber-950 font-semibold">Pausiert</span>}
                    <ChevronRight className="h-5 w-5 shrink-0 text-white/30" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
